import type { Translations } from './ru'

const en: Translations = {
  app: {
    title: 'Poker Dice',
    tagline: 'Digital scorecard · play face-to-face',
  },
  auth: {
    usernamePlaceholder: 'Username',
    login: 'Login',
    register: 'Register',
    createAccount: 'Create your account',
    welcomeBack: 'Welcome back!',
    orContinueWithEmail: 'Or continue with email',
    alreadyHaveAccount: 'Already have an account? Login',
    dontHaveAccount: 'Don\'t have an account? Register',
    playAsGuest: 'Play as Guest',
    emailPlaceholder: 'Email Address',
    passwordPlaceholder: 'Password',
    confirmPasswordPlaceholder: 'Confirm password',
    passwordsDoNotMatch: 'Passwords do not match',
    googleLogin: 'Google',
    appleLogin: 'Apple',
    googleComingSoon: 'Google login coming soon!',
    appleComingSoon: 'Apple login coming soon!',
    guestLoginFailed: 'Guest login failed',
    telegramFailed: 'Telegram sign-in failed. Check your connection and try again.',
    retry: 'Retry',
    forgotPassword: 'Forgot password?',
    resetInstructions: 'Enter your email and we\'ll send you a reset link.',
    enterEmailForReset: 'Enter your email to reset password',
    sendResetEmail: 'Send reset link',
    resetEmailSent: 'Email sent! Check your inbox.',
    backToLogin: 'Back to login',
    terms: 'By continuing, you agree to our Terms of Service and Privacy Policy.',
  },
  lobby: {
    newGame: 'New Game',
    codePlaceholder: 'ENTER CODE',
    recentGames: 'Recent Games',
    finished: 'finished',
    won: 'Won',
    noGames: 'No recent games',
    enter: 'Enter',
    rules: 'Rules',
    rating: 'Rating',
    offlineGame: 'Offline Game (Pass the Phone)',
    live: 'LIVE',
  },
  setup: {
    title: 'Setting Up Game',
    playerOrder: 'Player order',
    subtitle: 'Add all players before starting',
    gameCode: 'Game Code',
    addPlayer: 'Add Player',
    startGame: 'Start Game',
    waitingForAdmin: 'Waiting for the admin to start…',
    waiting: 'Waiting for start…',
    admin: '👑 Admin',
    telegramUser: 'Telegram user',
    guestPlayer: 'Guest player',
    visibilityOfScore: 'Score Visibility',
    realtime: 'Real-time',
    realtimeDesc: 'All players see scores live',
    finalOnly: 'After game',
    finalOnlyDesc: 'Scores hidden until game ends',
    gameMode: 'Game Mode',
    freeMode: 'Free',
    freeModeDesc: 'Physical dice, manual entry',
    virtualDiceMode: 'Virtual Dice',
    players: 'Players',
    qrScan: 'Scan to join the game',
    shareInvite: 'Share Invite',
    inviteMessage: '🎲 Join my Poker Dice game! Code: {{code}}',
    virtualDiceModeDesc: 'Roll in-app, scores auto-validated',
    joinAsPlayer: 'Join Game as Player',
    scanToJoinWeb: 'Scan QR to join this web session',
    host: 'Host',
    you: 'You',
    backToLobby: 'Lobby',
  },
  addPlayer: {
    title: 'Add Player',
    tabPast: '👥 Past players',
    tabCustom: '✏️ Custom name',
    tabSearch: '🔍 Search',
    search: 'Search…',
    searchPlaceholder: 'Username or name',
    noContacts: 'No past players yet',
    noMatches: 'No matches',
    noResults: 'No results found',
    nameLabel: 'Name (Papa, Mom, Sister…)',
    namePlaceholder: 'e.g. Papa',
    avatarLabel: 'Avatar emoji',
    add: 'Add {{name}}',
  },
  game: {
    editedBy: "{{editor}} changed {{target}}'s {{category}}",
    school: 'School',
    combinations: 'Combinations',
    total: 'TOTAL',
    bonus: 'bonus',
    scoresHidden: 'scores hidden',
    finishGame: 'Finish Game',
    showResults: 'Show Results',
    gameOver: '🏆 Game Over',
    confirmFinishTitle: 'Finish Game?',
    confirmFinishDesc: 'This will lock the scorecard and declare the winner.',
    cancel: 'Cancel',
    finish: 'Finish',
    close: 'Close',
    edit: 'EDIT',
    round: 'Round',
    compactView: 'Compact view',
    waitingForAdminFinish: 'Waiting for the admin to finish the game...',
    category: 'Category',
    inviteCode: 'Code',
    pendingUpdates: 'pending updates',
    diceTray: 'Dice Tray',
    roll: 'Roll',
    rollAgain: 'Roll Again',
    waitingForTurn: 'Waiting for other player...',
    swipeHint: 'Swipe to switch players',
    swipeToThrow: 'Swipe up to throw',
    keepSwiping: 'Keep going...',
    release: 'Release!',
    rethrow: 'To roll',
    reroll: 'To roll',
    keep: 'Kept',
    selectHint: 'Tap to keep · Swipe up to throw',
    reset: 'Reset',
  },
  notifications: {
    school_flunker: 'Flunker!!',
    school_graduated: 'Graduated!',
    school_honors: 'With Honors!',
    small_but_mine: 'Small But Mine!',
    main_game_started: 'Game started!',
    poker_rolled: 'POKER!!!',
    take_the_lead: 'Takes the lead!',
    last_round: 'Final round!',
    crossed_out: 'Tough luck…',
    achievementUnlocked: 'Achievement Unlocked!',
  },
  categories: {
    ones:          '1s',
    twos:          '2s',
    threes:        '3s',
    fours:         '4s',
    fives:         '5s',
    sixes:         '6s',
    one_pair:      '1 Pair',
    two_pairs:     '2 Pairs',
    three_of_kind: '3 of a Kind',
    straight1:     'Straight 1–5',
    straight2:     'Straight 2–6',
    full_house:    'Full House',
    four_of_kind:  'Four of a Kind',
    poker:         'Poker',
    garbage:       'Garbage',
  },
  hints: {
    ones:          'Sum of 1s · target: 3',
    twos:          'Sum of 2s · target: 3',
    threes:        'Sum of 3s · target: 3',
    fours:         'Sum of 4s · target: 3',
    fives:         'Sum of 5s · target: 3',
    sixes:         'Sum of 6s · target: 3',
    one_pair:      'Sum of the matching pair',
    two_pairs:     'Sum of both pairs',
    three_of_kind: 'Sum of three matching dice',
    straight1:     'Fixed: 15 pts',
    straight2:     'Fixed: 20 pts',
    full_house:    'Sum of all 5 dice + 10',
    four_of_kind:  'Sum of 4 matching dice + 20',
    poker:         'Sum of all 5 dice + 50',
    garbage:       'Sum of any dice',
  },
  scoreInput: {
    howMany: 'How many {{n}}s did you roll?',
    crossOut: '✕ Cross Out',
    confirm: 'Record',
    suggested: 'Suggested from dice',
    tip: 'Tip',
    clear: 'Clear',
    scoreFixed: '✓ Score {{pts}} pts',
    record: 'Record',
    selectCount: 'Select count',
    fromHand: 'From hand (×2)',
    flatBonus: 'bonus (not doubled from hand)',
  },
  winner: {
    title: 'КАТАЛА',
    wins: 'Winner!',
    place1: '🥇 1st Place',
    place2: '🥈 2nd Place',
    place3: '🥉 3rd Place',
    newGame: 'New Game',
    backToLobby: 'Back to Lobby',
    pts: 'pts',
    shareResults: 'Share results',
    shareResultsMessage: '🎲 Results for {{title}}:\n{{results}}',
    notifyPlayers: 'Notify all players via Bot',
    rematch: 'Rematch',
  },
  tournament: {
    newTournament: 'New Tournament',
    standings: 'Tournament Standings',
    gameCount: 'Number of Games',
    nextGame: 'Next Game',
    tournamentOver: 'Tournament Over',
    grandTotal: 'Grand Total',
    winsColumn: 'Wins',
    winnerMode: 'Winner decided by',
    byPoints: 'Total points',
    byWins: 'Games won',
    byPointsDesc: 'The player with the highest total score across all games wins',
    byWinsDesc: 'The player who wins the most games takes the title (points break ties)',
    points: 'Points',
    game: 'Game',
    round: 'Round',
    matrix: 'Matrix',
    rounds: 'Tournament Rounds List',
    create: 'Create Tournament',
    join: 'Join Tournament',
    confirmKick: 'Remove {{name}} from tournament?',
    waitingParticipants: 'Waiting for participants...',
    adminStart: 'Admin needs to start the next game',
    progress: 'Progress',
    active: 'Tournament active',
    finished: 'Tournament finished',
    suddenDeath: 'Sudden Death Round!',
    spectatorMode: 'Spectator Mode',
    scanToWatchStandings: 'Scan to watch standings',
    consistencyBonus: 'Consistency Bonus',
    comebackKid: 'Comeback Kid',
    theGrinder: 'The Grinder',
    finishTournament: 'Finish Tournament',
    champion: 'Tournament Champion!',
    finalStandings: 'Final Standings',
    shareResults: 'Share Tournament Results',
    backToHome: 'Back to Home',
  },
  stats: {
    title: 'Statistics',
    globalRank: 'globally',
    leaderboard: 'Leaderboard',
    myStats: 'My Stats',
    recentGames: 'Recent Games',
    noGames: 'No finished games yet',
    player: 'Player',
    avg: 'Avg',
    best: 'Best',
    noPersonal: 'Play some games to see your stats!',
    wins: 'Wins',
    avgScore: 'Avg Score',
    bestScore: 'Best Game',
    gamesPlayed: 'Games',
    winRate: 'Win Rate',
    scoreBreakdown: 'Score Breakdown',
    avgSchool: 'Avg School',
    avgCombos: 'Avg Combos',
    worstGame: 'Worst Game',
    totalScore: 'Total points scored',
    h2h: 'Head to Head',
    selectOpponent: 'Select opponent...',
    draws: 'Draws',
    achievements: 'Achievements',
    elo: 'Rating',
    profile: 'Profile',
    viewProfile: 'View Profile',
  },
  settings: {
    diceColors: 'Dice Style',
    default: 'Default',
    colorful: 'Colorful',
    sound: 'Sound',
    style: 'Style',
    colorfulDice: 'Colorful dice',
    theme: 'Theme',
    tableTheme: 'Table Theme',
  },
  rules: {
    title: 'Game Rules',
    overview: 'Overview',
    overviewDesc: 'Poker Dice is a game for 2 or more players. Everyone rolls 5 dice up to three times per turn to collect the best combinations and fill their scorecard.',
    turnFlow: 'Turn Flow',
    step1Title: 'Roll & Hold',
    step1Desc: 'Make your first roll. Pick the dice you want to keep and reroll the rest (up to 2 times).',
    step2Title: 'Recording Score',
    step2Desc: 'After any roll, pick a category to record your score. One turn = one filled cell.',
    bonusTitle: '«From Hand» Bonus (×2)',
    bonusDesc: 'Got a combination on your very first roll? Your dice points are doubled!',
    schoolTitle: 'School (Upper Section)',
    schoolDesc: 'Target is to collect exactly 3 dice of each value (1 to 6). Score is recorded as the difference from target:',
    ptsOnSixes: '+6 pts (on 6s)',
    ptsPenaltyOnSixes: '-6 pts (on 6s)',
    schoolBonuses: 'School Bonuses',
    allRowsPositive: 'All rows positive: +100 pts',
    allRowsZeroed: 'All rows zeroed (✕): +50 pts',
    negativePenalty: 'Negative total balance: -50 pts penalty',
    restrictionHint: '⚠️ Rounds 1–3 are restricted to the School section only.',
    combosTitle: 'Combinations (Lower Section)',
    onePair: 'One Pair',
    onePairDesc: '2 matching dice. Sum of the pair',
    twoPairs: 'Two Pairs',
    twoPairsDesc: '2×2 matching. Sum of all four',
    threeOfKind: 'Three of a Kind',
    threeOfKindDesc: '3 matching dice. Sum of the three',
    straight: 'Straight',
    straightDesc: '1-2-3-4-5 (15 pts) or 2-3-4-5-6 (20 pts)',
    fullHouse: 'Full House',
    fullHouseDesc: '3 + 2 of a kind. Sum + 10',
    pokerDesc: '4 or 5 of a kind. Sum + 20/+50',
    garbageTitle: 'Garbage (Chance)',
    garbageDesc: 'Sum of any dice',
    mustCrossOut: 'If nothing fits, you must cross out one category (records 0 pts).',
    gotIt: 'Got it!',
  },
}

export default en
